import { Type } from 'class-transformer';
import {
  ArrayMaxSize,
  IsArray,
  IsBoolean,
  IsIn,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  Matches,
  Max,
  MaxLength,
  ValidateNested,
} from 'class-validator';
import { FIELD_VALUE_SOURCES, type FieldValueSource } from '../../label/field-value-source';
import { NullableTrimmedStringTransform } from '../../validation/nullable-trimmed-string.transformer';
import { OptionalStringTransform } from '../../validation/optional-string.transformer';
import { ADDITIVE_FUNCTIONS, type AdditiveFunction } from './additive-function.enum';
import { BREAKDOWN_EXEMPTIONS, type BreakdownExemption } from './breakdown-exemption.enum';
import { IngredientAllergenDto } from './ingredient-allergen.dto';
import { E_NUMBER_PATTERN, IngredientComponentDto } from './ingredient-component.dto';
import {
  INGREDIENT_PHYSICAL_KINDS,
  type IngredientPhysicalKind,
} from './ingredient-physical-kind.enum';
import { IngredientVerticalOriginEntryDto } from './ingredient-vertical-origin-entry.dto';

export class CreateIngredientDto {
  @IsString()
  @IsNotEmpty()
  name!: string;

  /** Denominazione legale in etichetta, se diversa dal nome di anagrafica; vuoto = nessuna. */
  @IsOptional()
  @IsString()
  @NullableTrimmedStringTransform()
  @MaxLength(200)
  labelName?: string | null;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  synonyms?: string[];

  @IsIn(INGREDIENT_PHYSICAL_KINDS)
  physicalKind!: IngredientPhysicalKind;

  @IsOptional()
  @IsIn(ADDITIVE_FUNCTIONS)
  additiveFunction?: AdditiveFunction;

  @IsOptional()
  @IsString()
  @OptionalStringTransform()
  @Matches(E_NUMBER_PATTERN)
  additiveENumber?: string;

  @IsOptional()
  @IsIn(BREAKDOWN_EXEMPTIONS)
  breakdownExemption?: BreakdownExemption;

  /** Deliberate tri-state (see the `Ingredient.isCompound` schema comment):
   * `undefined` here leaves the column at its `NULL` default (nobody has
   * declared yet); `false` is an explicit "declared simple" act, distinct
   * from an exemption; `true` means the breakdown gate expects components or
   * a `breakdownExemption`. */
  @IsOptional()
  @IsBoolean()
  isCompound?: boolean;

  @IsOptional()
  @IsNumber()
  @IsPositive()
  densityGPerMl?: number;

  @IsOptional()
  @IsIn(FIELD_VALUE_SOURCES)
  densitySource?: FieldValueSource;

  @IsOptional()
  @IsNumber()
  @IsPositive()
  pieceWeightG?: number;

  @IsOptional()
  @IsIn(FIELD_VALUE_SOURCES)
  pieceWeightSource?: FieldValueSource;

  @IsOptional()
  @IsNumber()
  @IsPositive()
  @Max(1)
  ediblePortionFactor?: number;

  @IsOptional()
  @IsIn(FIELD_VALUE_SOURCES)
  ediblePortionSource?: FieldValueSource;

  @IsOptional()
  @IsString()
  @OptionalStringTransform()
  brand?: string | null;

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => IngredientAllergenDto)
  allergens?: IngredientAllergenDto[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => IngredientComponentDto)
  components?: IngredientComponentDto[];

  @IsOptional()
  @IsArray()
  @ArrayMaxSize(11)
  @ValidateNested({ each: true })
  @Type(() => IngredientVerticalOriginEntryDto)
  verticalOrigin?: IngredientVerticalOriginEntryDto[];
}
